<template>
  <section
    class="card"
    :class="{
      ['card--active']: isActive,
      ['card--visible']: isVisible,
      ['card--animated']: isAnimated,
      ['card--active-animated']: isActiveAnimated,
    }"
  >
    <header>
      <img class="card__avatar" :src="testimonial.avatar" alt="" />
      <h3 class="card__title" v-text="testimonial.title" />
    </header>
    <div class="card__content">
      <dotcom-rating class="card__rating" :value="testimonial.rating" />
      <blockquote class="card__quote" v-text="testimonial.quote" />
    </div>
    <footer class="card__footer">
      <div class="card__name" v-text="testimonial.name" />
      <div class="card__read-more">
        <a
          v-if="testimonial.link"
          class="card__link"
          data-track-as="testimonial.readmore"
          :tabindex="isVisible ? 0 : -1"
          :href="testimonial.link"
          v-text="$t('readMore')"
        />
      </div>
    </footer>
  </section>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import DotcomRating from './Rating'

export default {
  name: 'DotcomCard',
  components: { DotcomRating },
  props: {
    testimonial: {
      type: Object,
      default() {
        return {}
      },
    },
    language: {
      type: String,
      default: 'de',
    },
    isActive: Boolean,
    isVisible: Boolean,
    isAnimated: Boolean,
    isActiveAnimated: Boolean,
  },
  setup(props) {
    const loadLabels = lang => import(`../i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
}
</script>

<style lang="postcss" scoped>
.card {
  --testimonial-height: 396px;
  --testimonial-active-height: 396px;
  --testimonial-avatar-size: 105px;
  --testimonial-padding-factor: 1;

  display: flex;
  flex-direction: column;
  min-height: var(--testimonial-height);
  padding: 0 var(--spacing-4) var(--spacing-6);
  background: var(--c-primary-neutral-2);
  text-align: center;

  opacity: 0;
  transform-style: preserve-3d;
  transform: translateZ(-200px);
  margin-top: calc(var(--testimonial-avatar-size) / 2);

  .container-wrapper.warm-gray &,
  .container-wrapper.sanitas-green &,
  .container-wrapper.sanitas-dark-green & {
    background-color: var(--c-primary-neutral-3);
  }
}

.card--active-animated {
  transition: min-height 300ms ease-in-out, padding 300ms ease-in-out;

  .card__avatar,
  .card__name,
  .card__title,
  .card__rating,
  .card__quote {
    transition: margin-bottom 300ms ease-in-out;
  }
}

.card--animated {
  transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
}

.card--active-animated.card--animated {
  transition: min-height 300ms ease-in-out, transform 300ms ease-in-out,
    opacity 300ms ease-in-out, padding 300ms ease-in-out;
}

.card:not(.card--animated),
.card--visible {
  opacity: 1;
  transform: translateZ(0);
}

.card--active {
  min-height: var(--testimonial-active-height);
}

.card__avatar,
.card__name {
  margin-bottom: var(--spacing-6);
}

.card__title,
.card__rating,
.card__quote {
  margin-bottom: var(--spacing-4);
}

.card__title,
.card__quote {
  word-break: break-word;
}

.card__avatar {
  border-radius: 50%;
  height: var(--testimonial-avatar-size);
  width: var(--testimonial-avatar-size);
  object-fit: cover;
  margin-top: calc(var(--testimonial-avatar-size) / -2);
}

.card__title {
  font-family: 'TheMix Bold', sans-serif;
  font-size: 20px;
  line-height: 24px;
  color: var(--c-primary-color-2);
}

.card__content {
  font-size: 15px;
  color: var(--c-primary-neutral-1);
}

.card__quote {
  margin: 0 0 var(--spacing-4);

  &::before {
    content: '«';
  }

  &::after {
    content: '»';
  }
}

.card__footer {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card__name {
  font-weight: bold;
}

.card__read-more {
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
}

.card__link {
  border-bottom: 1px solid var(--c-primary-color-3);
  color: var(--c-primary-color-3);

  &:hover {
    border-bottom-color: var(--c-tint-color-1);
    color: var(--c-tint-color-1);
  }
}

@media (--md) {
  .card {
    --testimonial-height: 366px;
    --testimonial-active-height: 414px;
    padding: 0 calc(var(--spacing-4) * var(--testimonial-padding-factor))
      var(--spacing-8);
  }

  .card--active {
    --testimonial-padding-factor: 1.92;

    .card__name {
      margin-bottom: var(--spacing-8);
    }
  }

  .card:not(.card--active) {
    .card__avatar,
    .card__name {
      margin-bottom: var(--spacing-4);
    }

    .card__title,
    .card__rating,
    .card__quote {
      margin-bottom: var(--spacing-2);
    }
  }
}

@media (--lg) {
  .card {
    --testimonial-height: 372px;
    --testimonial-active-height: 427px;
  }

  .card--active {
    --testimonial-padding-factor: 2.3;
  }
}
</style>
